import { type ReactNode, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover'
import { Button } from '@/components/ui/Button'
import type { FindingLocation } from '@/utils/bundleFindings'

interface ExplainPopoverProps {
  title: string
  reasoning: string[]
  jumpTo?: FindingLocation
  onJump?: (location: FindingLocation) => void
  children: ReactNode
}

const CARD_LABELS: Record<FindingLocation['card'], string> = {
  bundle: 'bundle',
  identity: 'Identity card',
  skills: 'Skills card',
  tools: 'Tools card',
  workflows: 'Workflows card',
}

/**
 * P4.T9: clickable explanation popover. Wraps a trigger child (icon
 * or chip), surfaces multi-line reasoning + optional jump-to-card
 * link. Reusable by P4.T10/T11 per spec §8.
 */
export function ExplainPopover({ title, reasoning, jumpTo, onJump, children }: ExplainPopoverProps) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80 p-4 space-y-2">
        <h4 className="font-semibold text-sm">{title}</h4>
        {reasoning.map((para, idx) => (
          <p key={idx} className="text-sm text-foreground">{para}</p>
        ))}
        {jumpTo && onJump && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { onJump(jumpTo); setOpen(false) }}
          >
            Jump to {CARD_LABELS[jumpTo.card]}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  )
}
